import React, { createContext, useContext } from 'react';
import type { PropsWithChildren } from 'react';

// Matches `@radix-ui/react-portal`'s `container` prop so callers can pass
// through Radix Portal `container` values without narrowing casts.
export type PortalContainer = Element | DocumentFragment;

// Container that overlay components (tooltip, popover, dropdown, dialog, etc.)
// should portal into. When `undefined`, Radix falls back to its default
// (`document.body`), so this is a no-op for normal apps.
//
// It exists for microfrontends that render inside a Shadow DOM: portalling to
// `document.body` would escape the shadow root and lose the design system
// styles (and become subject to the host page's styles). Providing a container
// inside the shadow root keeps overlays styled and isolated.
const PortalContainerContext = createContext<HTMLElement | undefined>(
	undefined
);

export function PortalContainerProvider({
	container,
	children,
}: PropsWithChildren<{ container: HTMLElement | undefined }>) {
	return (
		<PortalContainerContext.Provider value={container}>
			{children}
		</PortalContainerContext.Provider>
	);
}

// Returns the portal container from context, or `undefined` to use the Radix
// default. Pass the result to a Radix `Portal`'s `container` prop.
export function usePortalContainer(): HTMLElement | undefined {
	return useContext(PortalContainerContext);
}

// Resolves where an overlay should portal, preserving backwards compatibility:
// 1. Explicit `portalContainer` / Radix `container` prop (e.g. drawer-local
//    mounts in iframe apps, or a caller-provided Element)
// 2. `PortalContainerProvider` context (Shadow DOM embeds)
// 3. `undefined` → Radix/Vaul default (`document.body`) for iframe / standalone apps
export function useResolvedPortalContainer(
	portalContainer?: PortalContainer | null
): PortalContainer | undefined {
	const contextContainer = usePortalContainer();
	return portalContainer ?? contextContainer;
}
